import { useEffect, useRef, useState } from 'react'
import { getLightspeedApi, loadClaimsReport, saveClaims } from '../lightspeed/pos-api'
import { captureAccountDump } from '../debug/accountDump'
import { FIXTURE_CUSTOMER, FIXTURE_ITEMS } from './marketplace-fixtures'
import { restoreWallet, type Wallet } from './marketplace-model'

interface Capturer {
  capture: (event: string, properties?: Record<string, unknown>) => void
}

/** The customer's points for THIS sale, surviving the extension being closed and reopened.
 *
 * Lives apart from the screen because it is a distinct concern with a distinct lifetime: the panel
 * is unmounted constantly at a till, while the claims belong to the ticket. Bundling the two is
 * what let claim state quietly vanish on every reopen — the cashier came back to a full balance and
 * could give the same free item away twice.
 *
 * `appliedLines` and `addedLines` are refs, not state: nothing on screen is drawn from them (they
 * are bookkeeping for undo), and making them state would re-render the whole grid for no visible
 * change.
 */
export function useSaleClaims(posthog: Capturer | undefined, onError: (message: string) => void) {
  const [wallet, setWallet] = useState<Wallet>(() => ({
    balance: FIXTURE_CUSTOMER.points_balance,
    claimed: new Set(),
  }))

  /** reward_id → the line its discount landed on, so undo can toggle that exact line back off. */
  const appliedLines = useRef<Record<string, string>>({})
  /** reward_id → true when WE rang the product up. Undo can remove our discount but not the line
   * itself (no remove-item call exists), so this is what tells the cashier to void it. */
  const addedLines = useRef<Record<string, boolean>>({})
  /** The open sale's identifier, which scopes the store so a new ticket starts clean. */
  const accountNumber = useRef<string | undefined>(undefined)

  // Come back to what this sale already had. Runs once on mount — the sale cannot change under us
  // while the panel is up.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const api = getLightspeedApi()
        const account = await api.getCurrentAccount()
        if (cancelled) return
        accountNumber.current = account.number
        const report = await loadClaimsReport(api, account.number)
        // Reported every open, restored or not: "reopening shows nothing" is invisible otherwise,
        // and the interesting cases (nothing stored / stored under a different sale / the store
        // never answered) are indistinguishable on screen.
        posthog?.capture('webext_restore_claims', {
          had_raw: report.hadRaw,
          stored_account: report.storedAccount,
          current_account: report.currentAccount,
          account_missing: !account.number,
          timed_out: report.timedOut,
          // What the host handed back — 'object' on device, 'string' in a browser. Reported
          // because assuming it was a string is precisely what silently broke every restore.
          raw_type: report.rawType,
          error: report.error,
          restored: report.claims.length,
          line_count: (account.lines ?? []).length,
        })
        // Paired with the restore report: if the account identifier differs from the stored one,
        // this dump is what says whether the POS really opened a different sale or just renamed it.
        void captureAccountDump(posthog, 'open', { restored: report.claims.length })
        const claims = report.claims
        if (cancelled || claims.length === 0) return
        for (const c of claims) {
          if (c.lineId) appliedLines.current[c.rewardId] = c.lineId
          if (c.added) addedLines.current[c.rewardId] = true
        }
        // Re-checked right here, immediately before the only setter: every await above is a chance
        // for the screen to have closed under us, and writing state into an unmounted panel is how
        // a stale balance survives into the next customer.
        if (cancelled) return
        setWallet(
          restoreWallet(
            FIXTURE_ITEMS,
            FIXTURE_CUSTOMER.points_balance,
            claims.map((c) => c.rewardId),
          ),
        )
      } catch {
        // No readable sale (plain browser, or the POS refused): start fresh rather than block the
        // panel — a failed restore must not stop the cashier serving the customer in front of them.
      }
    })()
    return () => {
      cancelled = true
    }
    // `posthog` is a stable client from the provider, so naming it here does not re-run the
    // restore — but leaving it out hides a real dependency.
  }, [posthog])

  /** Write the claimed set out. Called in full each time, so a cancel is just a shorter list. */
  function persistClaims(claimed: ReadonlySet<string>) {
    const claims = [...claimed].map((rewardId) => ({
      rewardId,
      lineId: appliedLines.current[rewardId] ?? '',
      added: !!addedLines.current[rewardId],
    }))
    try {
      saveClaims(getLightspeedApi(), accountNumber.current, claims)
    } catch (e) {
      // The discount is already on the sale; failing to record it means a reopen will offer the
      // reward again. Surface it rather than leaving the cashier to discover it later.
      onError(e instanceof Error ? e.message : String(e))
    }
  }

  return { wallet, setWallet, appliedLines, addedLines, persistClaims }
}
